import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import { getEssay } from '../../data/essays';
import { ArrowLeft } from 'lucide-react';
import PullQuote from '../../components/essays/PullQuote';

const essay = getEssay('the-space-between')!;

const TheSpaceBetween = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": essay.title,
    "description": essay.subtitle,
    "datePublished": essay.date,
    "dateModified": essay.dateModified ?? essay.date,
    "author": {
      "@type": "Person",
      "name": essay.author,
      "url": "https://www.linkedin.com/in/kent-frazier/",
    },
    "publisher": {
      "@type": "Organization",
      "name": "ParadoxEdge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.paradoxedge.com/lovable-uploads/180210a9-9a73-4038-a36b-f373598ef48a.png",
      },
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.paradoxedge.com/essays/${essay.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${essay.title} | Essays`}
        description={essay.subtitle}
        canonicalUrl={`/essays/${essay.slug}`}
        ogType="article"
        ogImage={essay.ogImage}
        ogImageAlt="The Space Between: an essay by Kent H. Frazier"
        publishedTime={essay.date}
        modifiedTime={essay.dateModified ?? essay.date}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        <main className="flex-1">
          <article className="py-20 sm:py-28">
            <div className="container-mobile max-w-[42rem]">
              {/* Back link */}
              <Link
                to="/essays"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 text-sm text-foreground/90 hover:text-primary transition-colors mb-10"
              >
                <ArrowLeft className="w-4 h-4" />
                All essays
              </Link>

              {/* Header */}
              <header className="mb-12 sm:mb-16">
                <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-primary font-semibold mb-6">
                  Essay
                </p>
                <h1 className="mb-6">{essay.title}</h1>
                <p className="font-display text-xl sm:text-2xl text-foreground/95 leading-snug mb-8 italic">
                  {essay.subtitle}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground border-t border-border pt-6">
                  <span className="font-semibold text-foreground">By {essay.author}</span>
                  <span aria-hidden="true">·</span>
                  <time dateTime={essay.date}>{essay.dateDisplay}</time>
                  <span aria-hidden="true">·</span>
                  <span>{essay.readingMinutes} min read</span>
                </div>
              </header>

              {/* Body */}
              <div className="essay-prose">
                {essay.summary && (
                  <aside
                    aria-label="In short"
                    className="not-prose mb-10 rounded-sm border-l-2 border-primary bg-secondary/40 px-6 py-5"
                  >
                    <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-2">
                      In short
                    </p>
                    <p className="text-foreground/85 leading-relaxed">
                      {essay.summary}
                    </p>
                  </aside>
                )}

                <p>She had been on my team for a week.</p>
                <p>
                  She had come over from the client side of the business and wanted to build a career in learning and development. I was new to the company and her new boss. We were in our first one on one, something came up, and she started to cry.
                </p>
                <p>Then she caught herself and apologized.</p>
                <p>
                  I asked her when it was okay to cry. She said not here. Not at work, not in front of a new boss. At home, maybe. In her room. Alone, where no one could see.
                </p>
                <p>I told her I understood. Then I asked if she was open to another possibility.</p>
                <p>She said yes.</p>
                <p>I said, what if it's okay to cry whenever we are crying?</p>
                <p>I watched her whole body let go.</p>
                <p>
                  Nothing else happened in that room. No exercise. No plan. Two people, and something in her that had been held tight for years finally wasn't.
                </p>
                <p>
                  Months later we were walking back from lunch and she asked me how I had become a decent manager.
                </p>
                <p>I told her the truth. I became one because I had been a bad one, and because someone quit.</p>
                <p>
                  Earlier in my career I had a woman on my team I never built any trust with. One day my boss called me into his office and told me she had resigned. She had not resigned to me. She went around me, told him, and told him why. The way I worked with her was the reason she was leaving.
                </p>
                <p>I felt punched in the gut. My heart hurt. I was sad and scared.</p>
                <p>I became keenly aware of needing a new way of being and leading.</p>
                <p>
                  Everything I thought I knew about leading came apart in that conversation. I had to look at who I was being, with myself, with the people who worked for me, with what I thought I was chasing.
                </p>
                <p>
                  And this learning is why, years later, I could sit across from a crying woman in her first week and not need her to stop.
                </p>
                <p>
                  The Center for Creative Leadership has been studying leadership for fifty years. Their new report is called <em>The Space Between People</em>. What they found is that trust built on role and title and delivering what you promised is brittle. It works until something goes wrong, and then it snaps. Trust built between two people who have actually met each other holds. It survives mistakes. It survives pressure.
                </p>
                <p>
                  Martin Buber was a philosopher who spent his life on one question: what happens between two people. He said the most important thing we ever do is meet each other. Turn toward another person and take them in as a person, rather than as something useful to us. He had a name for what opens up when that happens. He called it <em>the between</em>. It belongs to neither person. It only exists while both of them are in it.
                </p>
                <p>That is where she and I were, in that first one on one.</p>
                <p>I believe that is where a person becomes real.</p>
                <p>
                  The trust between us held for years, and it held because she left that room differently than she came in, and so did I. That is not something you can install. Two people who work well together are still two people who could walk away. Two people who have met each other have a deeper connection.
                </p>
                <p>
                  I was able to meet this woman more skillfully, because someone had walked out of a job rather than keep working for me.
                </p>
                <p>
                  Years later I was going through a hard stretch and I called her and asked what she remembered from our time together. She did not pause.
                </p>
                <PullQuote>
                  Kent, you taught us it was okay to be fully human at work.
                </PullQuote>
                <p>Our meeting in the space between, and her reflection, changed everything for me.</p>
                <p>
                  That sentence became the question I have been working on ever since. What does it mean to be more fully human at work? What does it mean to be fully human with ourselves, with the people we love, with the people we lead, in an age of artificial everything?
                </p>
                <p>I don't have it figured out.</p>
                <p>
                  I have a sense we live our way into it. That it asks us to get brave enough to stop needing to be right, to win, to know. And to meet each other in the field.
                </p>

                <blockquote className="not-prose my-14 sm:my-16 pl-6 border-l-2 border-primary/60">
                  <div className="font-display italic text-foreground/90 text-lg sm:text-xl leading-relaxed space-y-1">
                    <p>Out beyond ideas</p>
                    <p>of wrong-doing and right-doing,</p>
                    <p>there is a field. I'll meet you there.</p>
                    <p>When the soul lies down in that grass,</p>
                    <p>the world is too full to talk about.</p>
                    <p>Ideas, language, even the phrase</p>
                    <p>"each other"</p>
                    <p>don't make any sense.</p>
                  </div>
                  <footer className="mt-4 text-sm text-muted-foreground not-italic">
                    Rumi, translated by Coleman Barks
                  </footer>
                </blockquote>
              </div>

              {/* Author block */}
              <aside className="mt-20 pt-10 border-t border-border">
                <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-4">
                  About the author
                </p>
                <p className="text-foreground leading-relaxed">
                  Kent Frazier is the founder of ParadoxEdge. A former HR executive and Certified Integral Master Coach, he has spent three decades inside the work of leadership, partnering with senior leaders to develop the inner capacities and outer capabilities that meet the moment.
                </p>
                <div className="mt-8">
                  <a
                    href="https://calendly.com/kentfrazier/30-minutes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 py-3 text-sm font-semibold rounded-full transition-all duration-300"
                  >
                    Begin a Conversation
                  </a>
                </div>
              </aside>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default TheSpaceBetween;